import React, { useEffect, useMemo, useState } from "react";
import { MdViewList, MdGridView, MdAltRoute } from "react-icons/md";

import { useFilesContext } from "../../context/domain/files/FilesContext";

import { getFolderInfo, getFileLink } from "../../services/api/files";

import PhotoExplorer from "../../features/files/drive/photoExplorer";
import Explorer from "../../features/files/drive/Explorer";

/**
 * Shared Drive browser for project folders and files.
 *
 * @returns {JSX.Element}
 */
const Drive = () => {
  const {
    folderData,
    setFolderData,
    isGridView,
    setIsGridView,
    isDynamicView,
    setIsDynamicView,
  } = useFilesContext();

  useEffect(() => {
    document.title = "R&S Shared Drive";
  }, []);

  const structure = useMemo(
    () => ({
      level0: [
        {
          title: "Folders",
          type: "explorer",
          urlAppend: "",
          folders: "projects",
          files: "none",
          dynamicDisplay: "list",
          comments: false,
        },
      ],
      level1: [
        {
          type: "explorer",
          title: "Plans",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
          dynamicDisplay: "grid",
          comments: false,
        },
        {
          type: "photoExplorer",
          title: "Photos",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
          dynamicDisplay: "grid",
          comments: true,
        },
      ],
      level2: [
        {
          type: "explorer",
          title: "Name",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
        },
      ],
    }),
    []
  );

  const [currentPath, setCurrentPath] = useState("Shared/Projects");
  const [searchQuery, setSearchQuery] = useState("");

  const isHome = currentPath === "Shared/Projects";
  const level = "level" + (currentPath.split("/").length - 2);

  /**
   * Navigate into a folder and load its Egnyte listing.
   *
   * @param {string} folderName - Folder path or name segment.
   * @returns {Promise<void>}
   */
  const handleFolderClick = async (folderName) => {
    const newPath = folderName.startsWith("/")
      ? folderName.substring(1)
      : folderName;
    const newLevel = "level" + (newPath.split("/").length - 2);
    try {
      if (structure[newLevel] && structure[newLevel][0]) {
        const folderInfo = await getFolderInfo(
          newPath + structure[newLevel][0]["urlAppend"]
        );
        setFolderData(folderInfo);
        setCurrentPath(newPath);
      } else {
        console.error(`Invalid structure level: ${newLevel}`);
      }
    } catch (error) {
      console.error("Failed to fetch folder info:", error);
    }
  };

  /**
   * Navigate up one folder level.
   *
   * @returns {Promise<void>}
   */
  const handleBackClick = async () => {
    if (isHome) return;
    const newPath = currentPath.substring(0, currentPath.lastIndexOf("/"));
    try {
      const folderInfo = await getFolderInfo(newPath);
      setFolderData(folderInfo);
      setCurrentPath(newPath);
    } catch (error) {
      console.error("Failed to fetch folder info:", error);
    }
  };

  /**
   * Open a file link from Egnyte in a new tab.
   *
   * @param {string} filePath - Egnyte file path.
   * @returns {Promise<void>}
   */
  const handleFileClick = async (filePath) => {
    try {
      const data = await getFileLink(filePath);
      if (data["links"][0].url) {
        window.open(data.links[0].url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error fetching file link:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-inter">
      <div className="p-4">
        <div className="mx-auto mb-4 flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0 md:justify-end">
          <h1 className="pl-0 text-center text-2xl font-bold text-gray-950 md:text-left">
            R&S Shared Drive
          </h1>
          <div className="flex w-full items-center space-x-4 md:w-auto">
            <input
              type="text"
              placeholder="Search filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-[32px] w-full rounded-lg border border-gray-300 px-4 py-1 md:w-64"
            />
            <div className="flex h-[32px] min-w-[96px] items-center overflow-hidden rounded-full border border-gray-300">
              <div
                className={`flex items-center p-2 ${
                  !isGridView && !isDynamicView ? "bg-blue-100" : "bg-white"
                }`}
                role="button"
                title="List view"
                aria-label="List view"
                onClick={() => {
                  setIsGridView(false);
                  setIsDynamicView(false);
                }}
              >
                <MdViewList className="text-gray-700" />
              </div>
              <div
                className={`flex items-center p-2 ${
                  isGridView ? "bg-blue-100" : "bg-white"
                }`}
                role="button"
                title="Grid view"
                aria-label="Grid view"
                onClick={() => {
                  setIsGridView(true);
                  setIsDynamicView(false);
                }}
              >
                <MdGridView className="text-gray-700" />
              </div>
              <div
                className={`flex items-center p-2 ${
                  isDynamicView ? "bg-blue-100" : "bg-white"
                }`}
                role="button"
                title="Auto view"
                aria-label="Auto view"
                onClick={() => {
                  setIsDynamicView(true);
                  setIsGridView(false);
                }}
              >
                <MdAltRoute className="text-gray-700" />
              </div>
            </div>
          </div>
        </div>
        <div className="m-auto">
          <div className="flex flex-row rounded-l-lg border-b bg-gray-200 font-semibold text-gray-600 shadow-md">
            <div
              className={`rounded-l-lg px-2 ${
                !isHome ? "hover:bg-gray-300" : "text-gray-400"
              }`}
            >
              <button
                className="min-w-[75px] p-2 text-center"
                onClick={handleBackClick}
                disabled={isHome}
              >
                {!isHome ? "← Back" : "Home"}
              </button>
            </div>
            <div className="rounded-r-l col-span-3 w-full truncate bg-white p-2 pl-4 text-left text-gray-400">
              {currentPath.split("/").map((part, index, arr) => {
                const truncatedPart =
                  part.length > 10 ? `${part.slice(0, 10)}...` : part;
                const pathToHere = arr.slice(0, index + 1).join("/");
                return (
                  <span
                    key={index}
                    className={`${
                      arr.length > 2 && index !== arr.length - 1
                        ? "cursor-pointer"
                        : ""
                    } ${
                      index < arr.length - 1 ? "text-blue-500" : "text-gray-400"
                    } ${index !== arr.length - 1 ? "truncate" : ""}`}
                    onClick={() => handleFolderClick(pathToHere)}
                  >
                    {index > 0 &&
                      (index !== arr.length - 1 ? truncatedPart : part)}
                    {index < arr.length - 1 && index !== 0 && " / "}
                  </span>
                );
              })}
            </div>
          </div>
          {structure[level]?.map((layout, index) =>
            layout.type === "explorer" ? (
              <Explorer
                key={index}
                title={layout["title"]}
                folderData={folderData}
                structure={layout}
                isGridView={
                  isDynamicView && layout["dynamicDisplay"] === "grid"
                    ? true
                    : isGridView
                }
                searchQuery={searchQuery}
                handleFolderClick={handleFolderClick}
                handleFileClick={handleFileClick}
                comments={layout["comments"]}
              />
            ) : layout.type === "photoExplorer" ? (
              <PhotoExplorer
                key={index}
                title={layout["title"]}
                path={currentPath}
                isGridView={
                  isDynamicView && layout["dynamicDisplay"] === "grid"
                    ? true
                    : isGridView
                }
                searchQuery={searchQuery}
                handleFileClick={handleFileClick}
                comments={layout["comments"]}
              />
            ) : null
          )}
        </div>
      </div>
    </div>
  );
};

export default Drive;
